<template>
    <div class="auth-wrapper">
      <main class="auth-main d-flex align-items-center justify-content-center">
        <div class="card shadow-sm border-0 auth-card">
          <div class="auth-card-grid">
            <aside class="security-column">
              <span class="security-kicker">Conseils de sécurité</span>
              <h2 class="security-title">Protégez l’accès à votre stock</h2>

              <ul class="security-list">
                <li>
                  <span class="security-icon">
                    <i class="fa-solid fa-shield-halved"></i>
                  </span>
                  <span>Évitez d’utiliser le même mot de passe que sur d’autres sites.</span>
                </li>
                <li>
                  <span class="security-icon">
                    <i class="fa-solid fa-key"></i>
                  </span>
                  <span>Utilisez au moins 12 caractères, avec une majuscule, une minuscule, un chiffre et un symbole.</span>
                </li>
                <li>
                  <span class="security-icon">
                    <i class="fa-solid fa-user-lock"></i>
                  </span>
                  <span>Gardez ce mot de passe privé, même entre utilisateurs de la boutique.</span>
                </li>
              </ul>
            </aside>

            <section class="form-column">
              <div class="text-center mb-4">
                <img
                  src="../assets/logo.png"
                  class="auth-logo"
                  alt="Suite241 Gestion Commerciale"
                />
                <h3 class="fw-bold mb-1">Sécurisez votre compte</h3>
                <p class="text-muted mb-0">
                  Vous devez changer le mot de passe par défaut avant d’utiliser Suite241 Gestion Commerciale.
                </p>
              </div>

              <div v-if="successMessage" class="alert alert-success py-2 text-center">
                {{ successMessage }}
              </div>

              <div v-if="errorMessage" class="alert alert-danger py-2 text-center">
                {{ errorMessage }}
              </div>

              <form @submit.prevent="handleChangePassword">
                <div class="mb-3">
                  <label class="form-label fw-semibold">Mot de passe actuel</label>
                  <input
                    v-model="form.current_password"
                    type="password"
                    class="form-control form-control-lg"
                    placeholder="Entrez votre mot de passe actuel"
                    required
                  />
                </div>

                <div class="mb-3">
                  <label class="form-label fw-semibold">Nouveau mot de passe</label>
                  <input
                    v-model="form.new_password"
                    type="password"
                    class="form-control form-control-lg"
                    placeholder="Choisissez un nouveau mot de passe"
                    minlength="12"
                    maxlength="72"
                    autocomplete="new-password"
                    required
                  />
                </div>

                <div class="mb-4">
                  <label class="form-label fw-semibold">Confirmer le nouveau mot de passe</label>
                  <input
                    v-model="form.new_password_confirmation"
                    type="password"
                    class="form-control form-control-lg"
                    placeholder="Répétez le nouveau mot de passe"
                    required
                  />
                </div>

                <button
                  type="submit"
                  class="btn btn-primary btn-lg w-100"
                  :disabled="isLoading"
                >
                  <span v-if="isLoading"><i class="fa fa-spinner fa-spin me-2" aria-hidden="true"></i>Enregistrement…</span>
                  <span v-else><i class="fa fa-check me-2" aria-hidden="true"></i>Valider et continuer</span>
                </button>
              </form>
            </section>
          </div>
        </div>
      </main>
      <AppFooter />
    </div>
  </template>
  
  <script lang="ts">
  import { defineComponent, reactive, ref } from 'vue';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import AppFooter from '@/components/AppFooter.vue';
  import { forceBrowserNavigation } from '@/support/navigationFallback';
  
  export default defineComponent({
    name: 'ChangePasswordView',
    components: {
      AppFooter,
    },
  
    setup() {
      const store = useStore();
      const router = useRouter();
  
      const form = reactive({
        current_password: '',
        new_password: '',
        new_password_confirmation: '',
      });
  
      const isLoading = ref(false);
      const errorMessage = ref('');
      const successMessage = ref('');

      const routeHref = (route: { name: string }) => {
        try {
          return router.resolve(route).href;
        } catch {
          return '';
        }
      };

      const fallbackToBrowserNavigation = (href: string) => {
        if (!href) {
          errorMessage.value = 'Mot de passe modifié, mais la redirection automatique a échoué. Vous pouvez quitter cette page.';
          return;
        }

        try {
          forceBrowserNavigation(href);
        } catch {
          errorMessage.value = 'Mot de passe modifié, mais la redirection automatique a échoué. Vous pouvez quitter cette page.';
        }
      };
  
      /**
       * Appelle l’API /auth/change-password via Vuex.
       * Si succès :
       *  - met à jour token + user dans le store
       *  - redirige selon le rôle.
       */
      const handleChangePassword = async () => {
        isLoading.value = true;
        errorMessage.value = '';
        successMessage.value = '';

        let response: { data?: { user?: { role?: string } } } | undefined;

        try {
          response = await store.dispatch('auth/changePassword', {
            current_password: form.current_password,
            new_password: form.new_password,
            new_password_confirmation: form.new_password_confirmation,
          });
        } catch (error: unknown) {
          const responseError = error as {
            response?: {
              data?: {
                errors?: Record<string, string | string[]>;
              };
            };
          };
          const errors = responseError.response?.data?.errors;

          if (errors) {
            const messages = Object.values(errors)
              .flat()
              .join(' ');
            errorMessage.value = messages || 'Erreur lors du changement de mot de passe.';
          } else {
            errorMessage.value = 'Une erreur est survenue.';
          }
          isLoading.value = false;
          return;
        }

        try {
          const routeName = response?.data?.user?.role === 'caissier' ? 'CashierSales' : 'Dashboard';
          const targetRoute = { name: routeName };
          const href = routeHref(targetRoute);

          try {
            await router.replace(targetRoute);

            if (router.currentRoute.value.name === 'ChangePassword') {
              fallbackToBrowserNavigation(href);
            }
          } catch {
            fallbackToBrowserNavigation(href);
          }
        } catch {
          errorMessage.value = 'Mot de passe modifié, mais la redirection automatique a échoué. Vous pouvez quitter cette page.';
        } finally {
          isLoading.value = false;
        }
      };
  
      return {
        form,
        isLoading,
        errorMessage,
        successMessage,
        handleChangePassword,
      };
    },
  });
  </script>
  
  <style scoped>
  .auth-wrapper {
    --change-password-security-bg: #0b508c;

    background: var(--change-password-security-bg);
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    width: 100%;
  }

  .auth-main {
    flex: 1 1 auto;
    padding: 1rem;
  }

  .auth-card {
    width: 100%;
    max-width: 900px;
    border-radius: 12px;
    overflow: hidden;
  }

  .auth-card-grid {
    display: grid;
    grid-template-columns: minmax(240px, 0.85fr) minmax(320px, 1.15fr);
    min-height: 560px;
  }

  .security-column {
    background: var(--change-password-security-bg);
    color: #ffffff;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 2rem;
  }

  .security-kicker {
    color: rgba(255, 255, 255, 0.78);
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0;
    margin-bottom: 0.75rem;
    text-transform: uppercase;
  }

  .security-title {
    font-size: 1.65rem;
    font-weight: 800;
    line-height: 1.25;
    margin-bottom: 1.75rem;
  }

  .security-list {
    display: grid;
    gap: 1rem;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .security-list li {
    align-items: flex-start;
    display: grid;
    gap: 0.85rem;
    grid-template-columns: 2.2rem 1fr;
    line-height: 1.45;
  }

  .security-icon {
    align-items: center;
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.24);
    border-radius: 8px;
    display: inline-flex;
    height: 2.2rem;
    justify-content: center;
    width: 2.2rem;
  }

  .form-column {
    padding: 2rem;
  }

  .auth-logo {
    display: inline-block;
    margin-bottom: 1rem;
    width: 100px;
  }

  @media (max-width: 767.98px) {
    .auth-card-grid {
      grid-template-columns: 1fr;
      min-height: auto;
    }

    .security-column {
      padding: 1.5rem;
    }

    .security-title {
      font-size: 1.35rem;
      margin-bottom: 1.25rem;
    }

    .form-column {
      padding: 1.5rem;
    }
  }
  </style>
  
